import type { EngineState } from './types';

export const FNV_OFFSET = 0x811c9dc5;
export const FNV_PRIME = 0x01000193;

/** h * 16777619 은 double 정밀도가 날아가 다른 값이 나온다. 반드시 Math.imul. */
export function fnv1a(text: string): number {
  let h = FNV_OFFSET;
  for (let i = 0; i < text.length; i++) {
    h = Math.imul(h ^ text.charCodeAt(i), FNV_PRIME) >>> 0;
  }
  return h >>> 0;
}

export function fold(h: number, x: number): number {
  return Math.imul(h ^ (x >>> 0), FNV_PRIME) >>> 0;
}

export function toHex8(n: number): string {
  return (n >>> 0).toString(16).padStart(8, '0');
}

/** 키를 코드유닛 순으로 정렬한 결정론적 JSON. rulesHash 의 입력이다. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v === 'boolean' || typeof v === 'string') return JSON.stringify(v);
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) throw new Error('canonicalJson: 유한수만 허용한다');
    return JSON.stringify(v);
  }
  if (Array.isArray(v)) return `[${v.map(canonicalJson).join(',')}]`;
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    const keys = Object.keys(o).sort();
    return `{${keys.map((k) => `${JSON.stringify(k)}:${canonicalJson(o[k])}`).join(',')}}`;
  }
  throw new Error(`canonicalJson: 직렬화 불가 타입 ${typeof v}`);
}

export function rulesHash(rules: unknown): string {
  return toHex8(fnv1a(canonicalJson(rules)));
}

/**
 * 한 틱의 관측 가능한 상태를 32비트로 접는다. 매 틱 접는 롤링 해시다.
 * 성장 시계(growTicks·growPauseLeft)·pending·moveTicks 가 빠지면 리플레이 발산의 조기 경보를 잃는다.
 */
export function digestTick(s: EngineState): number {
  let h = FNV_OFFSET;
  h = fold(h, s.tick);
  h = fold(h, s.snake.dir);
  h = fold(h, s.snake.pending + 64); // 부호 있음 — 음수를 uint32 로 접기 전에 오프셋
  h = fold(h, s.readyTicksLeft);
  h = fold(h, s.clearTicksLeft);
  h = fold(h, s.word.index);
  h = fold(h, s.word.eaten);
  h = fold(h, s.word.wordTick);
  h = fold(h, s.word.movedTicks);
  h = fold(h, s.word.idealTicks);
  h = fold(h, s.word.relocations);
  h = fold(h, s.penalty.status);
  h = fold(h, s.penalty.ticksLeft);
  h = fold(h, s.penalty.deferTicksLeft);
  h = fold(h, s.phase === 'over' ? 2 : s.phase === 'revive' ? 1 : 0);
  h = fold(h, s.nominalMs);
  h = fold(h, s.moveTicks);
  h = fold(h, s.growTicks);
  h = fold(h, s.growPauseLeft);
  h = fold(h, s.lastSolveTick + 2);
  h = fold(h, s.peakLength);
  for (const c of s.snake.cells) h = fold(h, c);
  for (const t of s.tiles) {
    h = fold(h, t.cell);
    h = fold(h, t.ch.charCodeAt(0));
    h = fold(h, t.slot + 2);
  }
  h = fold(h, s.rng.quiz);
  h = fold(h, s.rng.layout);
  h = fold(h, s.rng.decoy);
  h = fold(h, s.rng.respawn);
  // 부활 — 플래그와 무관하게 항상 접는다(조건부로 접으면 해시 세계가 둘이 된다)
  const rv = s.revive;
  h = fold(h, rv.status);
  h = fold(h, rv.offered);
  h = fold(h, rv.used);
  h = fold(h, rv.correctIndex + 1);
  h = fold(h, rv.picked + 1);
  h = fold(h, rv.armTicksLeft);
  h = fold(h, rv.ticksLeft);
  h = fold(h, rv.revealTicksLeft);
  for (let i = 0; i < rv.questionId.length; i++) h = fold(h, rv.questionId.charCodeAt(i));
  h = fold(h, s.reviveTicks);
  h = fold(h, s.rng.revive);
  return h >>> 0;
}
